import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import {
  useEditEntityDataAttributesMutation,
  useGetEntityDataAttributesQuery,
} from "store/apis/entity";
import DoubleGreenTick from "static/images/DoubleGreenTick.svg";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Form, Formik } from "formik";
import { EntityDataAttribute } from "types/Models/entity";
import { debounce } from "utils/debouncing";
import isEqual from "lodash/isEqual";
import { BackendError } from "types/utils/error";
import { useToast } from "hooks/useToast";
import { useQuery } from "hooks/useQuery";
import Loader from "components/design/loader";

import { SalesTaxEntityDetailsBase } from "components/SalesTaxOnboardingSubmit/SalesTaxEntityDetailsBase";

export const SalesTaxEntityDetails = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast } = useToast();
  const query = useQuery();
  let entityId = query.get("entity") as string;

  const {
    data: currentEntityDetails = [],
    isLoading,
    isFetching,
    isSuccess,
  } = useGetEntityDataAttributesQuery(
    {
      groupId: groupId!,
      entityId,
    },
    {
      skip: !groupId || !entityId,
    }
  );
  const [editEntityDataAttributes, { isLoading: isUpdating }] =
    useEditEntityDataAttributesMutation();

  const isEmpty =
    currentEntityDetails?.length === 0 && (!isLoading || !isFetching);

  const initialValues = currentEntityDetails.reduce((acc, field) => {
    acc[field.name] = field.value;
    return acc;
  }, {} as Record<string, EntityDataAttribute["value"]>);

  const onChange = debounce(
    async ({
      name,
      value,
    }: {
      name: string;
      value: EntityDataAttribute["value"];
    }) => {
      const payload = { [name]: value };
      const initialValue = initialValues[name];

      const isSameValue = isEqual(
        JSON.stringify(value),
        JSON.stringify(initialValue)
      );

      if (isSameValue) {
        return;
      }

      try {
        await editEntityDataAttributes({
          groupId,
          entityId,
          payload,
        }).unwrap();
      } catch (error) {
        alertToast({ message: (error as BackendError).data?.error?.message });
      }
    }
  );

  return (
    <div className="t-flex t-justify-center">
      <div className="t-w-[650px]">
        <div className="t-flex-col t-justify-center t-items-center">
          <div className="t-flex t-items-center t-gap-x-4">
            <p className="t-text-title-h2-bold t-m-0">Business details</p>
            <Async.Root
              isLoading={isUpdating}
              customLoader={<Loader customType="secondary" />}
              isEmpty={isEmpty}
              isSuccess={isSuccess}
            >
              <Async.Empty>
                <></>
              </Async.Empty>
              <Async.Success>
                <span className="t-flex t-text-text-30 t-text-body-sm t-items-center t-gap-1.5 t-transition-all">
                  <img src={DoubleGreenTick} alt="Saved" />
                  Changes saved
                </span>
              </Async.Success>
            </Async.Root>
          </div>
          <p className="t-text-body-sm t-text-text-30 t-mt-1.5">
            This business information is required for enabling automated tax
            remittance.
          </p>
        </div>

        <Async.Root
          isLoading={isLoading}
          isSuccess={isSuccess}
          isEmpty={isEmpty}
        >
          <Async.Empty>
            <></>
          </Async.Empty>
          <Async.Success>
            <Formik
              initialValues={initialValues}
              onSubmit={() => {}}
              enableReinitialize
            >
              <Form className="t-mt-6 t-pb-6">
                <SalesTaxEntityDetailsBase onChange={onChange} />
              </Form>
            </Formik>
          </Async.Success>
        </Async.Root>
      </div>
    </div>
  );
};
